<template>
  <v-dialog v-model="open" max-width="600px" persistent>
    <v-card class="d-flex flex-column" style="max-height: 90vh;">
      <v-toolbar flat color="secondary">
        <v-toolbar-title class="text-white">
          {{ $t("labels.json-property") }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn color="white" icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <div class="pa-4">
        <v-form v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="10">
              <v-text-field v-model="jsonPath" readonly :label="$t('labels.json-property')" variant="outlined" required
                density="compact" name="jsonPath" hide-details="auto" />
            </v-col>
            <v-col cols="2" class="d-flex flex-column align-center justify-center pl-2">
              <v-tooltip location="bottom">
                <template #activator="{ props: tooltipProps }">
                  <v-btn icon size="x-small" color="primary" v-bind="tooltipProps" :disabled="disabledButtonSelectAll"
                    @click="handleSelectAllClick">
                    <v-icon size="large">
                      {{ objectArrayWildcardActive ? 'mdi-checkbox-multiple-marked' :
                      'mdi-checkbox-multiple-blank-outline' }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("strings.selectAllPrompt") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-form>
      </div>

      <div style="overflow-y: auto; overflow-x: hidden; flex: 1 1 auto;">
        <v-divider class="mt-3 mb-3" />
        <v-row class="px-4">
          <v-col cols="12">
            <v-treeview :items="treeItems" open-on-click indent-lines="simple">
              <template #prepend="{ item }">
                <v-checkbox-btn v-if="item.isPrimitiveArrayNode" :model-value="isPrimitiveArrayFullySelected(item)"
                  :indeterminate="isPrimitiveArrayPartiallySelected(item)" density="compact" hide-details @click.stop
                  @update:model-value="togglePrimitiveArraySelection(item, $event)" />
                <v-checkbox-btn v-else-if="item.isNumericLeaf" :model-value="isPathSelected(item.jsonPath)"
                  density="compact" hide-details @click.stop
                  @update:model-value="toggleLeafSelection(item.jsonPath, $event)" />
              </template>
            </v-treeview>
          </v-col>
        </v-row>
      </div>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn color="primary darken-1" variant="text" @click="close">
          {{ $t("labels.cancel") }}
        </v-btn>
        <v-btn color="secondary darken-1" variant="text" :disabled="!valid" @click="confirm">
          {{ $t("labels.insert") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="showAutoSelectSnackbar" color="info">
      {{ $t('strings.selectAllPrompt') }}
      <template #actions>
        <v-btn color="white" variant="text" @click="handleSelectAllClick">
          {{ $t("labels.selectAll") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: Boolean,
  jsonData: Object,
  initJsonPath: String
});

const emit = defineEmits(['update:modelValue', 'confirm']);

const open = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const jsonPath = ref('');
const selectedPaths = ref(new Set()); // Using Set for performance

const multiSelectMode = ref(false);
const objectArrayWildcardActive = ref(false);
const disabledButtonSelectAll = ref(true);
const showAutoSelectSnackbar = ref(false);

// ─── Selection Helpers ────────────────────────────────────────────────

// A value counts as "numeric" for selection purposes if it's an actual
// number, or a string that represents one (e.g. "42", "-3.5"). Empty
// strings and non-numeric strings don't count.
function isNumericLike(value) {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed !== '' && Number.isFinite(Number(trimmed));
  }
  return false;
}

function isPathSelected(path) {
  return selectedPaths.value.has(path);
}

function toggleLeafSelection(path, checked) {
  const newSet = new Set(selectedPaths.value);

  if (checked) {
    newSet.add(path);
  } else {
    newSet.delete(path);
  }

  selectedPaths.value = newSet;
  updateJsonPathFromSelection();
}

function updateJsonPathFromSelection() {
  const paths = Array.from(selectedPaths.value);

  if (paths.length === 0) {
    jsonPath.value = '';
    disabledButtonSelectAll.value = true;
    return;
  }

  if (paths.length === 1) {
    jsonPath.value = paths[0];
  } else {
    // Multi-selection: normalize paths
    const normalized = paths.map(p => p.replace(/\[\d+\]/g, '[*]'));
    const allSame = normalized.every(n => n === normalized[0]);

    if (allSame) {
      jsonPath.value = normalized[0];
    } else {
      // Different structures: keep only the last one
      jsonPath.value = paths[paths.length - 1];
      selectedPaths.value = new Set([jsonPath.value]);
    }
  }

  const hasObjArrayIdx = checkHasObjectArrayIndex(jsonPath.value);
  disabledButtonSelectAll.value = !hasObjArrayIdx;
  multiSelectMode.value = hasPrimitiveArrayWildcard(jsonPath.value);
  objectArrayWildcardActive.value = hasObjectArrayWildcard(jsonPath.value);

  if (hasObjArrayIdx && !objectArrayWildcardActive.value) {
    showAutoSelectSnackbar.value = true;
    setTimeout(() => { showAutoSelectSnackbar.value = false; }, 4000);
  }
}

// ─── Watchers ────────────────────────────────────────────────────────────────

watch(() => props.modelValue, (newVal) => {
  if (newVal && props.initJsonPath) {
    jsonPath.value = props.initJsonPath;

    // Initialize selection based on initial path
    const matches = getPathMatches(props.jsonData, props.initJsonPath);
    selectedPaths.value = new Set(matches);

    multiSelectMode.value = hasPrimitiveArrayWildcard(props.initJsonPath);
    objectArrayWildcardActive.value = hasObjectArrayWildcard(props.initJsonPath);
    disabledButtonSelectAll.value = !checkHasObjectArrayIndex(props.initJsonPath);

  }
});

// ─── Dialog actions ──────────────────────────────────────────────────────────

function close() {
  jsonPath.value = '';
  selectedPaths.value = new Set();
  multiSelectMode.value = false;
  objectArrayWildcardActive.value = false;
  disabledButtonSelectAll.value = true;
  showAutoSelectSnackbar.value = false;
  emit('update:modelValue', false);
}

function confirm() {
  emit('confirm', jsonPath.value);
  close();
}

// ─── Primitive array handling ────────────────────────────────────────────────

function isPrimitiveArrayFullySelected(item) {
  if (!item.childPaths || item.childPaths.length === 0) return false;
  return item.childPaths.every(p => selectedPaths.value.has(p));
}

function isPrimitiveArrayPartiallySelected(item) {
  if (!item.childPaths || item.childPaths.length === 0) return false;
  const selectedCount = item.childPaths.filter(p => selectedPaths.value.has(p)).length;
  return selectedCount > 0 && selectedCount < item.childPaths.length;
}

function togglePrimitiveArraySelection(item, checked) {
  if (!item.childPaths) return;

  const newSet = new Set(selectedPaths.value);

  if (checked) {
    item.childPaths.forEach(p => newSet.add(p));
    jsonPath.value = item.wildcardPath;
    multiSelectMode.value = true;
  } else {
    item.childPaths.forEach(p => newSet.delete(p));
    multiSelectMode.value = false;
  }

  selectedPaths.value = newSet;

  if (newSet.size > 0 && !checked) {
    jsonPath.value = Array.from(newSet)[0];
  } else if (newSet.size === 0) {
    jsonPath.value = '';
  }

  const hasObjArrayIdx = checkHasObjectArrayIndex(jsonPath.value);
  disabledButtonSelectAll.value = !hasObjArrayIdx;
  objectArrayWildcardActive.value = hasObjectArrayWildcard(jsonPath.value);

}

// ─── Select All button handler ───────────────────────────────────────────────

function handleSelectAllClick() {
  if (!jsonPath.value) return;

  const analysis = analyzePathArrays(props.jsonData, jsonPath.value);
  const tokens = parseJsonPath(jsonPath.value);
  if (!tokens) return;

  let newPath;

  if (objectArrayWildcardActive.value) {
    // Deactivate: replace wildcard with [0]
    const newTokens = tokens.map((token, idx) => {
      if (token.type === 'wildcard' && analysis.objectArrayIndices.includes(idx)) {
        return { type: 'index', index: 0 };
      }
      return token;
    });
    newPath = rebuildPath(newTokens);
    objectArrayWildcardActive.value = false;
  } else {
    // Activate: replace indices with [*] only for object arrays
    const newTokens = tokens.map((token, idx) => {
      if (token.type === 'index' && analysis.objectArrayIndices.includes(idx)) {
        return { type: 'wildcard' };
      }
      return token;
    });
    newPath = rebuildPath(newTokens);
    objectArrayWildcardActive.value = true;
  }

  jsonPath.value = newPath;
  const matches = getPathMatches(props.jsonData, newPath);
  selectedPaths.value = new Set(matches);

  multiSelectMode.value = hasPrimitiveArrayWildcard(newPath);
  showAutoSelectSnackbar.value = false;
}

// ─── Helper functions ────────────────────────────────────────────────────────

function analyzePathArrays(data, path) {
  const tokens = parseJsonPath(path);
  if (!tokens) return { objectArrayIndices: [], primitiveArrayIndices: [], tokens: [] };

  const objectArrayIndices = [];
  const primitiveArrayIndices = [];

  let current = data;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (token.type === 'key') {
      current = current?.[token.key];
    } else if (token.type === 'index' || token.type === 'wildcard') {
      if (Array.isArray(current)) {
        const firstElement = current[0];
        const isObjectArray = firstElement !== null &&
          typeof firstElement === 'object' &&
          !Array.isArray(firstElement);

        if (isObjectArray) {
          objectArrayIndices.push(i);
        } else if (isNumericLike(firstElement)) {
          primitiveArrayIndices.push(i);
        }

        current = token.type === 'wildcard' ? firstElement : current[token.index];
      }
    }
  }

  return { objectArrayIndices, primitiveArrayIndices, tokens };
}

function hasObjectArrayWildcard(path) {
  if (!path) return false;
  const analysis = analyzePathArrays(props.jsonData, path);
  const tokens = parseJsonPath(path);
  if (!tokens) return false;
  return analysis.objectArrayIndices.some(idx => tokens[idx]?.type === 'wildcard');
}

function hasPrimitiveArrayWildcard(path) {
  if (!path) return false;
  const analysis = analyzePathArrays(props.jsonData, path);
  const tokens = parseJsonPath(path);
  if (!tokens) return false;
  return analysis.primitiveArrayIndices.some(idx => tokens[idx]?.type === 'wildcard');
}

function checkHasObjectArrayIndex(path) {
  if (!path) return false;
  const analysis = analyzePathArrays(props.jsonData, path);
  return analysis.objectArrayIndices.length > 0;
}

function rebuildPath(tokens) {
  let path = '$';
  for (const token of tokens) {
    if (token.type === 'key') {
      path += formatPathKey(token.key);
    } else if (token.type === 'index') {
      path += `[${token.index}]`;
    } else if (token.type === 'wildcard') {
      path += '[*]';
    }
  }
  return path;
}

// ─── JSONPath utilities ──────────────────────────────────────────────────────

function formatPathKey(key) {
  return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) ? `.${key}` : `["${key}"]`;
}

function parseJsonPath(path) {
  if (typeof path !== 'string' || !path.startsWith('$')) return null;

  const tokens = [];
  let i = 1;

  while (i < path.length) {
    const char = path[i];

    if (char === '.') {
      let j = i + 1;
      while (j < path.length && /[a-zA-Z0-9_]/.test(path[j])) j++;
      if (j === i + 1) return null;
      tokens.push({ type: 'key', key: path.slice(i + 1, j) });
      i = j;
      continue;
    }

    if (char === '[') {
      const closeIdx = path.indexOf(']', i);
      if (closeIdx === -1) return null;

      const inside = path.slice(i + 1, closeIdx);
      if (inside === '*') {
        tokens.push({ type: 'wildcard' });
      } else if (/^\d+$/.test(inside)) {
        tokens.push({ type: 'index', index: Number(inside) });
      } else {
        const quotedMatch = inside.match(/^"(.+)"$/) || inside.match(/^'(.+)'$/);
        if (!quotedMatch) return null;
        tokens.push({ type: 'key', key: quotedMatch[1] });
      }
      i = closeIdx + 1;
      continue;
    }

    return null;
  }

  return tokens;
}

function evaluateJsonPath(data, path) {
  const tokens = parseJsonPath(path);
  if (!tokens) return [];

  let entries = [{ value: data, path: '$' }];

  for (const token of tokens) {
    const nextEntries = [];

    for (const entry of entries) {
      if (token.type === 'key') {
        if (entry.value && typeof entry.value === 'object' && token.key in entry.value) {
          nextEntries.push({
            value: entry.value[token.key],
            path: `${entry.path}${formatPathKey(token.key)}`,
          });
        }
      } else if (token.type === 'index') {
        if (Array.isArray(entry.value) && token.index >= 0 && token.index < entry.value.length) {
          nextEntries.push({
            value: entry.value[token.index],
            path: `${entry.path}[${token.index}]`,
          });
        }
      } else if (token.type === 'wildcard' && Array.isArray(entry.value)) {
        for (let index = 0; index < entry.value.length; index++) {
          nextEntries.push({
            value: entry.value[index],
            path: `${entry.path}[${index}]`,
          });
        }
      }
    }

    entries = nextEntries;
    if (entries.length === 0) break;
  }

  return entries;
}

function getPathMatches(data, path) {
  return evaluateJsonPath(data, path).map(e => e.path);
}

// ─── Tree builder ────────────────────────────────────────────────────────────

function transformRecursive(data, keyName = '', currentPath = '$', arrayIndex = null) {
  const escapeKey = (key) =>
    /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) ? `.${key}` : `["${key}"]`;

  if (Array.isArray(data)) {
    const isNumericPrimitiveArray = data.length > 0 && data.every(item => isNumericLike(item));
    const isObjectArray = data.length > 0 && data.every(item =>
      item !== null && typeof item === 'object' && !Array.isArray(item)
    );

    const children = data.map((item, i) =>
      transformRecursive(item, '', `${currentPath}[${i}]`, i)
    ).flat();

    if (keyName) {
      const node = {
        title: isNumericPrimitiveArray
          ? `${keyName} []`
          : isObjectArray
            ? `${keyName} []`
            : `${keyName} []`,
        children,
      };

      if (isNumericPrimitiveArray) {
        node.isPrimitiveArrayNode = true;
        node.childPaths = data.map((_, i) => `${currentPath}[${i}]`);
        node.wildcardPath = `${currentPath}[*]`;
      }

      return node;
    }
    return children;
  }

  if (typeof data === 'object' && data !== null) {
    const children = Object.entries(data).map(([key, val]) =>
      transformRecursive(val, key, `${currentPath}${escapeKey(key)}`, null)
    ).flat();

    if (arrayIndex !== null) {
      return {
        title: `[${arrayIndex}]`,
        children,
        isArrayItem: true,
      };
    }

    return keyName ? { title: keyName, children } : children;
  }

  const isNumeric = isNumericLike(data);
  const displayKey = keyName || (arrayIndex !== null ? `[${arrayIndex}]` : '');

  return {
    title: `${displayKey}: ${data}`,
    jsonPath: currentPath,
    isNumericLeaf: isNumeric,
  };
}
const valid = computed(() => !!jsonPath.value);
const treeItems = computed(() => transformRecursive(props.jsonData));
</script>